import { CHAT_ROUTES } from 'gcs-common/constants/routes';
import NotificationBadge from 'gcs-common/design-system/ui/notification-badge';
import { friendlyTimeOrDate } from 'gcs-common/helper/dateHelper';
import { getCurrentUserId } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { getSidebarCollapsed } from 'gcs-common/slices/uiState/uiStateSelectors';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import type { components } from '../../../../types/openapi';
import useNavigateWithQueryParams from '../../../hooks/useNavigateWithQueryParams';
import TicketAttachmentThumbnail from '../TicketAttachmentThumbnail/TicketAttachmentThumbnail';
import TicketStatus from '../TicketStatus/TicketStatus';

interface TicketListItemProps {
  id: string;
  status: components['schemas']['EtsStatusEnum'];
  title: string;
  previewImageUrl: string | undefined;
  createdAt: string | undefined;
  updatedAt: string | undefined;
  hasNotifications: boolean;
  creatorId: string;
  creatorName?: string;
}

const TicketListItem = (props: TicketListItemProps) => {
  const {
    id,
    status,
    title,
    hasNotifications,
    previewImageUrl = null,
    updatedAt = undefined,
    creatorId,
    creatorName = 'Unbekannt',
  } = props;
  const navigateWithQueryParams = useNavigateWithQueryParams();
  const { routeTicketId } = useParams();
  const ticketId = routeTicketId ?? '';

  const goToTicket = (ticketId: string) => {
    navigateWithQueryParams(CHAT_ROUTES.TICKET(ticketId), ['creatorIds']);
  };
  const isSidebarCollapsed = useSelector(getSidebarCollapsed);
  const currentUserId = useSelector(getCurrentUserId);
  const isActive = ticketId === id;
  const handleTicketKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      goToTicket(id);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={`flex w-full cursor-pointer items-center border-0 border-neutral-3 border-b border-solid py-2 ${
        isSidebarCollapsed ? 'justify-center' : 'justify-between'
      } ${isActive ? 'bg-neutral-3 hover:bg-neutral-3' : 'hover:bg-neutral-2'}`}
      onClick={() => goToTicket(id)}
      onKeyDown={handleTicketKeyDown}
    >
      <div className="pl-1">
        <NotificationBadge className={`size-[8px] ${hasNotifications ? '' : 'invisible'}`} />
      </div>
      <div className={isSidebarCollapsed ? '' : 'pl-2'}>
        <TicketAttachmentThumbnail thumbnail={previewImageUrl || ''} />
      </div>

      {!isSidebarCollapsed && (
        <>
          <div className="flex-1 overflow-hidden px-3">
            <span className="block w-full truncate text-ellipsis whitespace-nowrap text-base text-neutral-12">
              {title}
            </span>
            {creatorId !== currentUserId && <span className="text-neutral-11 text-sm">{creatorName}</span>}
          </div>
          <div className="flex flex-col items-end pr-4">
            <span className="truncate p-[2px] text-neutral-11 text-sm">{friendlyTimeOrDate(updatedAt)}</span>
            <div className="flex items-center justify-between gap-1">
              <TicketStatus status={status} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TicketListItem;
